import React from 'react';
import classNames from 'classnames';

import PrimaryButton from './buttons/PrimaryButton';
import Tooltip from './Tooltip';
import { formatCurrency } from '../../lib/utils/formatters';
import { getNetTermDays } from '../../lib/utils/terms';

const formatFeePct = (pct) => `${Number(pct).toFixed(2)}%`;

const feePctForTerm = (term, grandTotal) => {
  const authoritativePct = term.get('borrower_processing_fee_percent');
  if (authoritativePct !== undefined && authoritativePct !== null) {
    return Number(authoritativePct) * 100;
  }

  const fees = parseFloat(term.get('total_fees'));
  return grandTotal > 0 ? (fees / grandTotal) * 100 : 0;
};

const isSelectedTerm = (term, selectedTerm) =>
  selectedTerm?.term === term.get('term') &&
  selectedTerm?.term_period === term.get('term_period');

/**
 * Section card that renders all day-unit Net Terms (30 / 60 / 90) as selectable
 * chips following the ui-elements multi-decision flow pattern. Selecting a chip
 * expands an inline breakdown and Continue button within the card.
 *
 * Props:
 *   terms            – Immutable List of available day-unit ApprovedTerm records
 *   selectedTerm     – { term, term_period, payment } from parent state
 *   setSelectedTerm  – parent state setter
 *   onContinue       – called when the user clicks Continue (same as Terms.js `save`)
 *   cart             – shopping cart entity from Redux
 *   isLoading        – disables the Continue button while routing
 */
export default function NetTermsCard({
  terms,
  selectedTerm,
  setSelectedTerm,
  onContinue,
  cart,
  isLoading,
  showRateBadge = true,
}) {
  const activeChip = terms.find((t) => isSelectedTerm(t, selectedTerm));

  const handleCardClick = () => {
    if (!activeChip && terms.size > 0) {
      const first = terms.first();
      setSelectedTerm({
        term: first.get('term'),
        term_period: first.get('term_period'),
        payment: first.get('monthly_payment'),
      });
    }
  };

  const isCardActive = !!activeChip;

  const handleChipClick = (term) => {
    setSelectedTerm({
      term: term.get('term'),
      term_period: term.get('term_period'),
      payment: term.get('monthly_payment'),
    });
  };

  // Top-right badge: reflects the selected term's fee
  const badgeLabel = () => {
    if (!activeChip) return null;
    const fees = parseFloat(activeChip.get('total_fees'));
    if (fees === 0) return 'No fee';
    const grandTotal = parseFloat(
      cart?.get?.('grand_total') ??
        cart?.grand_total ??
        activeChip.get('total_amount') - fees
    );
    const pct = feePctForTerm(activeChip, grandTotal);
    return `${formatFeePct(pct)} fee`;
  };

  const badgeIsNoFee =
    isCardActive && parseFloat(activeChip.get('total_fees')) === 0;

  // Build a comma-separated list of terms for the subtitle e.g. "Net 30, 60 or 90 terms"
  const termsList = terms.map((t) => getNetTermDays(t)).toArray();
  const termsLabel =
    termsList.length <= 2
      ? termsList.join(' or ')
      : termsList.slice(0, -1).join(', ') +
        ' or ' +
        termsList[termsList.length - 1];

  const numCols = Math.min(terms.size, 3);

  return (
    <div
      className={classNames(
        'mb-4 border rounded-2xl p-5 bg-white shadow-sm transition-all',
        isCardActive
          ? 'border-[3px] border-primary-blue'
          : 'border-zinc-300 hover:border-blue-300 cursor-pointer'
      )}
      onClick={handleCardClick}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-1">
        <div>
          <h2 className="text-base sm:text-lg font-bold">Net Terms</h2>
          <p className="text-xs sm:text-sm text-secondary-gray mt-0.5">
            Pay in full in one payment within {termsLabel} days.
          </p>
        </div>
        {showRateBadge && isCardActive && (
          <span
            className={classNames(
              'text-xs font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ml-3',
              badgeIsNoFee
                ? 'bg-background-success text-success border-green-200'
                : 'bg-blue-50 text-primary-blue border-blue-200'
            )}
          >
            {badgeLabel()}
          </span>
        )}
      </div>

      {/* Term chips */}
      <div className="mt-3 mb-2">
        {isCardActive && (
          <p className="text-xs text-secondary-gray font-medium mb-2">
            Choose your term
          </p>
        )}
        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: `repeat(${numCols}, minmax(0, 1fr))` }}
        >
          {terms.map((term, idx) => {
            const termDays = getNetTermDays(term);
            const fees = parseFloat(term.get('total_fees'));
            const totalAmt =
              parseFloat(term.get('total_amount')) ||
              parseFloat(cart?.grand_total);
            const grandTotal = totalAmt - fees;
            const feePct = feePctForTerm(term, grandTotal);
            const isSelected = isSelectedTerm(term, selectedTerm);

            return (
              <button
                key={term.get('id')}
                onClick={(e) => {
                  e.stopPropagation();
                  handleChipClick(term);
                }}
                className={classNames(
                  'border rounded-lg p-3 text-left transition-all',
                  isSelected
                    ? 'border-[2px] border-primary-blue bg-secondary-blue'
                    : 'border-primary-border-gray hover:border-blue-300'
                )}
              >
                <div className="text-sm font-semibold text-primary">
                  {termDays} days
                </div>
                <div
                  className={classNames(
                    'text-xs mt-0.5',
                    fees === 0
                      ? 'text-green-600 font-semibold'
                      : 'text-secondary-gray'
                  )}
                >
                  {fees === 0 ? 'No fee' : `+${formatFeePct(feePct)} fee`}
                </div>
                <div
                  className={classNames(
                    'text-sm font-bold mt-1',
                    isSelected ? 'text-primary-blue' : 'text-primary'
                  )}
                >
                  {formatCurrency(totalAmt)}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Inline breakdown — only visible when a chip is selected */}
      {activeChip && (
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-secondary-gray">Order total</span>
            <span className="font-medium">
              {formatCurrency(cart?.grand_total)}
            </span>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-primary flex items-center gap-1">
              Term fee
              <Tooltip
                tooltipText={
                  parseFloat(activeChip.get('total_fees')) === 0
                    ? 'Included'
                    : (() => {
                        const grandTotal = parseFloat(cart?.grand_total);
                        const pct = feePctForTerm(activeChip, grandTotal);
                        return `${formatFeePct(pct)} of order amount`;
                      })()
                }
                placement="top"
                triggerClass="px-0.5 hover:cursor-pointer hover:text-primary-blue text-xs"
                containerClass="rounded-2xl p-1.5 bg-gray-900 text-white text-xs w-max max-w-xs"
              >
                <span className="text-xs text-tertiary-gray">ⓘ</span>
              </Tooltip>
            </span>
            <span className="font-medium">
              {formatCurrency(activeChip.get('total_fees'))}
            </span>
          </div>

          <div className="flex items-center justify-between text-sm font-semibold pt-2 border-t border-primary-border-gray">
            <span>1 payment due in {getNetTermDays(activeChip)} days:</span>
            <span>
              {formatCurrency(
                parseFloat(activeChip.get('total_amount')) ||
                  parseFloat(cart?.grand_total)
              )}
            </span>
          </div>

          <PrimaryButton
            buttonText="Continue"
            buttonColor="bg-primary-blue"
            hoverClass="hover:bg-primary-blue-hover"
            disabled={isLoading}
            isLoading={isLoading}
            onClick={onContinue}
          />
        </div>
      )}
    </div>
  );
}
